import { useState } from 'react';
import { generatePlaygroundRoster, createRosterVersion } from '../../services/rosterService';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DatePicker } from '../ui/DatePicker';

const RosterPlayground = () => {
    const navigate = useNavigate();
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState<{ type: 'success' | 'error', message: string, details?: any } | null>(null);

    const handleGenerate = async () => {
        if (!startDate || !endDate) {
            setStatus({ type: 'error', message: 'Please select start and end dates' });
            return;
        }

        setLoading(true);
        setStatus(null);

        try {
            // 1. Create a new Draft Version
            const version = await createRosterVersion({
                startDate: new Date(startDate).toISOString(),
                endDate: new Date(endDate).toISOString(),
                notes: `Playground Generation ${format(new Date(), 'dd/MM/yyyy HH:mm')}`
            });

            // 2. Generate Patterns
            const result = await generatePlaygroundRoster({
                startDate,
                endDate,
                versionId: version.id
            });

            setStatus({
                type: 'success',
                message: 'Roster generated successfully!',
                details: result.stats
            });
        } catch (error: any) {
            setStatus({
                type: 'error',
                message: error.response?.data?.message || 'Failed to generate roster'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 max-w-4xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">Core Roster Playground</h1>
                <p className="text-gray-600">
                    Generate a complete core roster with standardized patterns:
                </p>
                <ul className="mt-2 list-disc list-inside text-gray-600 bg-blue-50 p-4 rounded-lg border border-blue-100">
                    <li><span className="font-semibold text-blue-800">Clearers:</span> 5 ON / 3 OFF (07:30 - 18:30)</li>
                    <li><span className="font-semibold text-blue-800">Controllers:</span> Alternating 4 ON / 4 OFF (07:30 - 19:30)</li>
                    <li><span className="font-semibold text-blue-800">Office Staff:</span> Monday - Friday (09:00 - 17:30)</li>
                </ul>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
                <div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Duration</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <DatePicker
                            label="Start Date"
                            value={startDate}
                            onChange={setStartDate}
                            placeholder="Pick start date"
                        />
                        <DatePicker
                            label="End Date"
                            value={endDate}
                            onChange={setEndDate}
                            min={startDate}
                            placeholder="Pick end date"
                        />
                    </div>
                </div>

                {status && (
                    <div className={`p-4 rounded-lg ${status.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                        <div className="flex items-center gap-2 font-medium mb-1">
                            {status.type === 'success' ? (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            )}
                            {status.message}
                        </div>
                        {status.details && (
                            <div className="mt-2 text-sm ml-7 grid grid-cols-2 gap-2">
                                <div>Office Shifts: {status.details.office}</div>
                                <div>Controller Shifts: {status.details.controllers}</div>
                                <div>Clearer Shifts: {status.details.clearers}</div>
                                <div className="font-bold border-t border-green-200 pt-1 mt-1 col-span-2">Total: {status.details.total}</div>
                            </div>
                        )}
                        {status.type === 'success' && (
                            <div className="mt-4 ml-7">
                                <button
                                    onClick={() => navigate('/roster')}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                                >
                                    View Roster Dashboard
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="pt-4 border-t border-gray-100 flex justify-end">
                    <button
                        onClick={handleGenerate}
                        disabled={loading}
                        className={`px-6 py-3 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:bg-blue-700 transition-all ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'}`}
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Generating...
                            </span>
                        ) : 'Generate Full Roster'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RosterPlayground;
